import { observable, computed, makeObservable } from 'mobx'
import _ from 'lodash'
import { strings, selectionTypes } from 'constants.js'
import api from 'api.js'
import Position from './common/Position'
import { catchError } from './ErrorStore'
import { selectionStore, dataStore } from './UIStore'
import { shapeTypes, defaultPolygonColor } from '../constants'
import { getDefaultGrayColor } from '../utils'
import Wkt from 'wicket/wicket'
import i18next from '../i18n'
// import RegionAnalysis from './RegionAnalysis'

require('script-loader!wicket/wicket')

class Isochrone {
  @observable name = ''
  @observable initial = true
  @observable invalid = observable.map()
  @observable position = new Position()
  @observable travelTime = 30
  @observable modeOfTransportation = 'Driving'
  @observable isochronePolygonGeoJson = null
  @observable region = null
  @observable.ref geoJSON
  @observable enableSubmit = true
  @observable color_id
  @observable transparency = 50
  @observable additional_info = []
  // @observable analysis = new RegionAnalysis()

  constructor (isUpdating, position, name, id, targetGroupId, travelTime, modeOfTransportation, color_id, transparency = 50) {
    makeObservable(this)
    if (isUpdating) {
      this.isUpdating = true
      this.position.latLng = position
      this.name = name
      this.initial = false
      this.id = id
      this.targetGroupId = targetGroupId
      this.travelTime = travelTime
      this.modeOfTransportation = modeOfTransportation
      this.color_id = color_id
      this.transparency = transparency
    }
  }

  computeGeoJSON (polygon) {
    const wkt = new Wkt.Wkt()
    this.geoJSON = wkt.read(polygon).toJson()
  }

  mapMousemove (e) {
    if (this.initial) {
      this.position.latLng = e.latlng
    }
  }

  mapMouseup (e) {
    this.initial = false
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.ISOCHRONE,
      data: { focusedInteraction: true }
    }
  }

  validate (targetGroup) {
    const group = targetGroup || (selectionStore.data && selectionStore.data.group) || (dataStore.targetGroups && dataStore.targetGroups[0])
    const geopolygons = group && group.geopolygons ? group.geopolygons : []
    if (!this.isUpdating && this.name && _.find(geopolygons, { name: this.name.toString() })) {
      this.invalid.set('name', strings.DUPLICATE_ELEMENT_NAME('geopolygon'))
      return false
    }

    this.invalid.clear()
    if (this.name === null || _.isEmpty(this.name.toString())) {
      this.invalid.set('name', strings.INVALID.REQUIRED)
    }
    if (this.position.longitude === null || _.isEmpty(this.position.longitude.toString())) {
      this.invalid.set('longitude', strings.INVALID.REQUIRED)
    }
    if (this.position.latitude === null || _.isEmpty(this.position.latitude.toString())) {
      this.invalid.set('latitude', strings.INVALID.REQUIRED)
    }
    return this.invalid.size === 0
  }

  @catchError
  async updateShape (newShape) {
    const wkt = new Wkt.Wkt()
    wkt.read(JSON.stringify(newShape.geometry))

    try {
      const result = await api.patchGeopolygon(this.id, {
        the_geom: wkt.write(),
        name: this.name,
        buffer: {
          travelTime: this.travelTime,
          point: this.position.geoJSON,
          modeOfTransportation: this.modeOfTransportation
        }
      })
      // this.updateGeoJSON(result.data.polygon)
      return result.data
    } catch (error) {
      selectionStore.clear()
    }
  }

  updateGeoJSON (polygon) {
    this.computeGeoJSON(polygon)
    // this.analysis = new RegionAnalysis()
    this.loadGeopolygonData()
  }

  @catchError async add (targetGroupId, geojson) {
    if (this.validate()) {
      const wkt = new window.Wkt.Wkt()
      // create turf polygon from geojson
      wkt.read(JSON.stringify(geojson['features'][0].geometry))
      const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)
      const result = await api.postGeopolygon({
        name: this.name,
        the_geom: wkt.write(),
        target_group: targetGroupId,
        type: shapeTypes.ISOCHRONE_SHAPE,
        buffer: {
          travelTime: this.travelTime,
          point: this.position.geoJSON,
          modeOfTransportation: this.modeOfTransportation
        },
        color: effectiveColorId,
        color_transparency: this.transparency,
      })
      return result.data
    } else {
      return null
    }
  }

  @computed get getColor () {
    const item = dataStore.markerColors.find(item => item.id === this.color_id)

    return item ? item.active_color : defaultPolygonColor
  }

  /** Add isochrone to draft mode instead of saving to backend immediately */
  async addToDraft (poi, geojson) {
    if (!this.validate()) {
      return false
    }

    if (!geojson || !geojson.features || !geojson.features[0]) {
      this.invalid.set('name', i18next.t('ITEM.GENERATE_ISOCHRONE_REQUIRED') || 'Please generate an isochrone first')
      return false
    }

    const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)

    // Store the polygon data in the POI draft state (this will trigger POI search)
    await poi.setDraftPolygon({
      name: this.name,
      geometry: geojson.features[0].geometry,
      color_id: effectiveColorId,
      transparency: this.transparency,
      type: 'isochrone',
      travelTime: this.travelTime,
      modeOfTransportation: this.modeOfTransportation,
      point: this.position.geoJSON
    })

    return true
  }
}
export default Isochrone
